/**
 * StrikeApprovalsBanner — the shop-floor "a style is one sign-off away from
 * production" surface, shown at the top of the supervisor Floor (`/production/floor`) and
 * the Station kiosk (`/station`).
 *
 * A style blocks production until its PP (pre-production / strike-file) sample is
 * approved. That control used to live only on the merchandiser's Style File, so
 * the floor waited on a "power user". This banner brings the same actionable
 * queue to where the line works: whoever holds `garment.samples.approve` can
 * Approve (→ production unlocks) or Reject in place; everyone else sees the
 * pending state read-only.
 *
 * Shared across two features, so it lives under `components/` (not either
 * feature) to avoid a sibling-feature import. Renders nothing when the queue is
 * empty or the workspace lacks the garment module.
 *
 * @see packages/domain/src/garment/samples — listPendingStrikeApprovals + the PP gate
 */
import { Stamp, Check, X } from "lucide-react";
import { Button, StatusPill, toast } from "@flint/ui";
import { trpc } from "../../lib/trpc.js";
import { usePermission } from "../../lib/permissions.js";
import { formatDate } from "../../lib/format.js";

export function StrikeApprovalsBanner({ className }: { className?: string }) {
  const utils = trpc.useUtils();
  const query = trpc.samples.pendingApprovals.useQuery();
  const canApprove = usePermission("garment.samples.approve");

  const invalidate = async () => {
    await Promise.all([
      utils.samples.pendingApprovals.invalidate(),
      // Approval flips the style to pp_approved → it enters the station
      // queues, the per-style read-model, and the stage-board grid. Broad
      // `wip.*` invalidation matches the four record surfaces (PR #160).
      utils.wip.invalidate(),
    ]);
  };

  const approve = trpc.samples.approve.useMutation({
    onSuccess: async () => {
      await invalidate();
      toast.success("Strike-file approved", { description: "Production is unlocked for this style." });
    },
    onError: (e: { message: string }) => toast.error("Couldn't approve", { description: e.message }),
  });
  const reject = trpc.samples.reject.useMutation({
    onSuccess: async () => {
      await invalidate();
      toast.success("Strike-file rejected", { description: "Submit a new revision when ready." });
    },
    onError: (e: { message: string }) => toast.error("Couldn't reject", { description: e.message }),
  });

  const items = query.data?.items ?? [];
  if (items.length === 0) return null;

  const busy = approve.isPending || reject.isPending;

  return (
    <section className={`rounded-xl border border-amber-200 bg-amber-50/60 p-4 ${className ?? ""}`}>
      <div className="mb-2.5 flex items-center gap-2">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-amber-100 text-amber-700">
          <Stamp className="h-4 w-4" />
        </span>
        <div>
          <h2 className="text-base font-semibold text-ink">Strike-file approvals waiting</h2>
          <p className="text-xs text-muted">
            {canApprove
              ? "Approve to unlock production, or reject to ask for a new sample."
              : "These styles are waiting on a pre-production sample sign-off."}
          </p>
        </div>
      </div>

      <ol className="flex flex-col gap-2">
        {items.map((it) => (
          <li
            key={it.submissionId}
            className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-amber-100 bg-paper px-3.5 py-2.5"
          >
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-sm font-semibold text-ink">{it.styleRef}</span>
                <span className="truncate text-sm text-ink-2">{it.name}</span>
                <StatusPill tone="muted" size="sm">
                  Sample · R{it.revision}
                </StatusPill>
              </div>
              <div className="mt-0.5 text-xs text-muted">
                {it.buyerName}
                {" · submitted "}
                {formatDate(it.submittedAt)}
                {it.exFactoryDate ? ` · ex-factory ${formatDate(it.exFactoryDate)}` : ""}
              </div>
            </div>

            {canApprove ? (
              <div className="flex shrink-0 items-center gap-2">
                <Button
                  size="sm"
                  intent="ghost"
                  disabled={busy}
                  onClick={() => reject.mutate({ submissionId: it.submissionId })}
                >
                  <X className="mr-1 h-3.5 w-3.5" /> Reject
                </Button>
                <Button
                  size="sm"
                  disabled={busy}
                  onClick={() => approve.mutate({ submissionId: it.submissionId })}
                >
                  <Check className="mr-1 h-3.5 w-3.5" /> Approve
                </Button>
              </div>
            ) : (
              <StatusPill tone="sky" size="sm" dot>
                Awaiting sign-off
              </StatusPill>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}
